import React, { useState, useEffect, useRef, createRef, useContext } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useWidgetChildrenSelector } from "components/Editor/hooks/useEditorSession";

import {
  updateWidgetProperty,
  updateWidgetOrder,
  unselectWidget,
  updateWidgetName,
  actionGroup,
  updateToolsSettings,
  arrangeWidget
} from "actions/editor";

import cx from "classnames";
import _ from "lodash";

import { Accordion, Button, Dropdown } from "react-bootstrap";
import { EditorContext } from "components/Editor";
import Truncate from 'react-truncate';

import Draggable from 'react-draggable';
import Portal from 'components/Portal';

import {
  PAGE, TEXT, IMAGE, BUTTON, SHAPE, IFRAME,
  HTML, LINE, RECTANGLE, ANCHOR, FORM, FORM_STEP,
  SECTION_COLUMN, SECTION, LIVE_ELEMENT, 
  CONTENT, CONTENT_CARD_TEMPLATE, CONTENT_EMPTY_MESSAGE, CONTENT_CARD, CONTENT_PAGINATION_BUTTON,
  EXTERNAL_FORM
} from "../../../../constants/widgetTypes";

import styles from "./layers.module.scss";
import Tooltip from "components/Tooltip";


export default function Layers({ root, style }) {
  const dispatch = useDispatch();

  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const [position, setPosition] = useState({
    x: 100,
    y: 300
  });

  const [filter, setFilter] = useState('all');

  useEffect(() => {
    calculateDialogPosition();
  }, []);

  const calculateDialogPosition = () => {
    var targetEl = document.querySelector(`#${editorSession} .editor_widget_scroll_container`);
    if (!targetEl) {
      return;
    }

    const rect = targetEl.getBoundingClientRect();

    const x = rect.width - 222 - 300;
    const y = rect.top + 70;

    setPosition({
      x: x,
      y: y
    });
  }

  const handlePositionChange = (e, ui) => {
    setPosition((position) => {
      return {
        x: position.x + ui.deltaX,
        y: position.y + + ui.deltaY
      };
    });
  }

  const handleClose = () => {
    dispatch(updateToolsSettings(editorSession, {
      layers: false
    }));
  }

  const renderFilter = (filter) => {
    var label = 'All Elements';
    switch (filter) {
      case 'visible': {
        label = 'Visible Elements';
        break;
      }
      case 'hidden': {
        label = 'Hidden Elements';
        break;
      }
      default: {
        label = 'All Elements';
        break;
      }
    }

    return label;
  }

  return (
    <Portal>
      <Draggable handle={`.${styles.dragHandle}`} bounds='parent' position={position} onDrag={handlePositionChange}>
        <div className={styles.layers} style={style}>
          <header className={styles.dragHandle}>
            <div className={styles.title}>Layers</div>
            <i className={cx(styles.draggabel, 'cleverstory-drag')} />
            <div className={styles.actions}>
              <Button className={styles.close} onClick={handleClose}>
                <i className='cleverstory-close' />
              </Button>
            </div>
          </header>
          <Dropdown className={styles.filter}>
            <Dropdown.Toggle className={styles.selected}>
              {renderFilter(filter)}
            </Dropdown.Toggle>

            <Dropdown.Menu className={styles.filterMenu}>
              <Dropdown.Item onClick={() => setFilter('all')}>{renderFilter('all')}</Dropdown.Item>
              <Dropdown.Item onClick={() => setFilter('visible')}>{renderFilter('visible')}</Dropdown.Item>
              <Dropdown.Item onClick={() => setFilter('hidden')}>{renderFilter('hidden')}</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <div className={styles.scrollContainer}>
            <Container id={root} breakpoint={breakpoint} filter={filter} />
          </div>
        </div>
      </Draggable>
    </Portal>
  );
}

const Container = ({ id, type, breakpoint, filter }) => {
  const dispatch = useDispatch();

  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const sortableRef = React.useRef(null);
  const sortableElementRef = React.useRef(null);

  var widgets = useWidgetChildrenSelector(editorSession, id);

  var visibleWidgets = (widgets || []).filter(widget => {
    const visible = _.get(widget, `properties.${breakpoint}.visible`);
    if (filter === 'visible') {
      return visible;
    } else if (filter === 'hidden') {
      return !visible;
    }

    return true;
  });

  if (type === SECTION || type === CONTENT) {
    visibleWidgets = _.orderBy(visibleWidgets, 'order', 'asc');
  }

  const selectedWidget = useSelector((state) => {
    const selected = _.get(state, `${editorSession}.present.selected`) || [];
    return _.size(selected) == 1 ? selected[0] : null;
  });

  // useEffect(() => {
  //   if (!sortableElementRef.current) {
  //     return;
  //   }

  //   sortableRef.current = new Sortable(sortableElementRef.current, {
  //     draggable: `.${styles.widget}`,
  //     handle: `.${styles.widget}`,
  //     delay: 300,
  //     mirror: {
  //       xAxis: false,
  //       yAxis: true,
  //       appendTo: 'body',
  //       constrainDimensions: true,
  //     },
  //   });
  // }, []);

  // useEffect(() => {
  //   sortableRef.current.on("sortable:stop", handleReorder);

  //   return () => {
  //     sortableRef.current.off("sortable:stop", handleReorder);
  //   };

  // }, [visibleWidgets]);

  const handleClick = (widget) => {
    const id = widget.uid;
    const visible = _.get(widget, 'properties.mobile.visible');
    if (breakpoint == 'mobile' && !visible) {
      return;
    }

    const element = document.querySelector(`#${editorSession} #${id}`);
    if (!element) {
      return;
    }

    const selecto = editorContext.editorSession.selecto.current;
    const mouseEvent = new MouseEvent("click", {
      bubbles: true,
      cancelable: true,
      view: window
    });

    selecto.clickTarget(mouseEvent, element);

    element.scrollIntoView({
      behavior: 'smooth',
      block: "end",
      inline: "end"
    });
  };

  const handleWidgetName = (widget, name) => {
    var id = widget.uid;
    dispatch(updateWidgetName(editorSession, id, name));
  }

  const handleReorder = (e) => {
    var oldIndex = e.data.oldIndex;
    var newIndex = e.data.newIndex;

    if (oldIndex === newIndex) {
      return;
    }

    var widget = visibleWidgets[oldIndex];

    var newWidget = visibleWidgets[newIndex];
    newIndex = widgets.findIndex(item => item.uid === newWidget.uid);

    dispatch(arrangeWidget(editorSession, widget, newIndex));
  };

  const toggleVisibility = (widget, visible) => {
    dispatch(updateWidgetProperty(editorSession, widget, breakpoint, {
      visible: visible
    }));

    if (visible) {
      dispatch(unselectWidget(editorSession, widget))
    }
  }

  const renderComponent = (widget) => {
    const type = _.get(widget, 'type');

    var component = null;
    switch (type) {
      case FORM: {
        component = FormWidget;
        break;
      }

      case FORM_STEP: {
        component = Widget;
        break;
      }

      case EXTERNAL_FORM: {
        component = ExternalFormWidget;
        break;
      }

      case CONTENT_CARD_TEMPLATE: {
        component = null;
        break;
      }

      case CONTENT_EMPTY_MESSAGE: {
        component = null;
        break;
      }

      default: {
        component = Widget;
        break;
      }
    }

    if (!component) {
      return false;
    }

    return React.createElement(component, {
      key: widget.uid,
      widgetProps: widget,
      active: widget.uid === selectedWidget,
      breakpoint: breakpoint,
      filter: filter,
      onClick: handleClick,
      onChange: handleWidgetName,
      onVisibilityChange: toggleVisibility
    });
  }

  return (
    <div className={styles.container} ref={sortableElementRef}>
      {visibleWidgets.map(widget => {
        return renderComponent(widget);
      })}
    </div>
  );
}

const Widget = ({ widgetProps, active, breakpoint, filter, onClick, onChange, onVisibilityChange }) => {
  const visible = _.get(widgetProps, `properties.${breakpoint}.visible`);

  return (
    <div className={styles.widget}>
      <WidgetItem
        key={widgetProps.uid}
        widgetProps={widgetProps}
        active={active}
        breakpoint={breakpoint}
        filter={filter}
        onClick={onClick}
        onChange={onChange}
        onVisibilityChange={onVisibilityChange}
      />
      {visible &&
        <div className={styles.children}>
          <Container id={widgetProps.uid} type={widgetProps.type} breakpoint={breakpoint} filter={filter} />
        </div>
      }
    </div>
  );
}

const WidgetItemComponent = ({ widgetProps, active, breakpoint, filter, onClick, onChange, onVisibilityChange }) => {
  const inputRef = React.useRef(null);
  const id = _.get(widgetProps, 'uid');
  const name = _.get(widgetProps, 'name');
  const visible = _.get(widgetProps, `properties.${breakpoint}.visible`);

  const [editable, setEditable] = useState(false);
  const [inputName, setInputName] = useState(name);

  useEffect(() => {
    setEditable(false);
  }, [active]);

  useEffect(() => {
    if (editable) {
      inputRef.current.focus();
    }
  }, [editable]);

  const renderIcon = () => {
    const type = _.get(widgetProps, 'type');

    switch (type) {
      case PAGE:
        return "cleverstory-page";

      case TEXT:
        return "cleverstory-text-layer";

      case BUTTON:
        return "cleverstory-button";

      case IMAGE:
        return "cleverstory-background";

      case SHAPE:
        return "cleverstory-shape";

      case LINE:
        return "cleverstory-shape";

      case RECTANGLE:
        return "cleverstory-shape";

      case IFRAME:
        return "cleverstory-html";

      case HTML:
        return "cleverstory-html";

      case ANCHOR:
        return "cleverstory-anchor"

      case HTML:
        return "cleverstory-html"

      case LIVE_ELEMENT:
        return "cleverstory-live-element"

      case FORM:
      case FORM_STEP:
      case EXTERNAL_FORM:
        return "cleverstory-forms"

      default:
        return "cleverstory-content"
    }
  };

  const canToggleVisibility = () => {
    const type = _.get(widgetProps, 'type');

    switch (type) {
      case SECTION_COLUMN:
        return false;

      case CONTENT_CARD:
        return false;

      case CONTENT_PAGINATION_BUTTON:
        return false;
    }

    return true;
  }

  const handleInputChange = (e) => {
    setInputName(e.target.value);
  }

  const updateWidgetName = () => {
    onChange(widgetProps, inputName);
    setEditable(false);
    setInputName(null);
  }

  const handleVisibilityChange = () => {
    onVisibilityChange(id, !visible);
  }

  return (
    <>
      <div
        className={cx(styles.widgetItem, {
          [styles.active]: active,
          [styles.hidden]: !visible
        })}
        onClick={(e) => {
          e.stopPropagation();
          onClick(widgetProps)
        }}
        onDoubleClick={(e) => {
          setInputName(name);
          setEditable(true);
        }}
      >
        <div className={styles.details}>
          <i className={cx(styles.icon, renderIcon())} />

          {editable &&
            <input
              ref={inputRef}
              type={'text'}
              className={styles.name}
              disabled={!active}
              value={inputName}
              onChange={handleInputChange}
              onBlur={() => updateWidgetName()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  updateWidgetName();
                }
              }}
            />
          }

          {!editable &&
            <span className={styles.name}>
              <Truncate lines={1}>{name}</Truncate>
            </span>
          }
        </div>
        <div className={styles.actions}>
          {canToggleVisibility() &&
            <Tooltip className={styles.tooltip} placement='top' content={visible ? `Hide in ${breakpoint}` : `Show in ${breakpoint}`}>
              <Button variant="info" onClick={() => handleVisibilityChange()}>
                <i className={cx(styles.icon, { 'cleverstory-view': visible, 'cleverstory-hide': !visible })} />
              </Button>
            </Tooltip>
          }
        </div>
      </div>
    </>
  );
}

function widgetComponentEqual(prevProps, nextProps) {
  var paths = ['active', 'breakpoint'];

  var next = _.pick(nextProps, paths);
  var previous = _.pick(prevProps, paths);

  var equals = _.isEqual(next, previous);
  if (!equals) {
    return false;
  }

  const previousWidget = _.get(prevProps, 'widgetProps');
  const nextWidget = _.get(nextProps, 'widgetProps');
  paths = ['name', 'type', 'properties.desktop.visible', 'properties.mobile.visible'];
  next = _.pick(nextWidget, paths);
  previous = _.pick(previousWidget, paths);

  equals = _.isEqual(next, previous);
  return equals;
}

const WidgetItem = React.memo(WidgetItemComponent, widgetComponentEqual);


const FormWidget = ({ widgetProps, active, breakpoint, filter, onClick, onChange, onVisibilityChange }) => {
  return (
    <WidgetItem
      key={widgetProps.uid}
      widgetProps={widgetProps}
      active={active}
      breakpoint={breakpoint}
      filter={filter}
      onClick={onClick}
      onChange={onChange}
      onVisibilityChange={onVisibilityChange}
    />
  )
}

const ExternalFormWidget = ({ widgetProps, active, breakpoint, filter, onClick, onChange, onVisibilityChange }) => {
  return (
    <WidgetItem
      key={widgetProps.uid}
      widgetProps={widgetProps}
      active={active}
      breakpoint={breakpoint}
      filter={filter}
      onClick={onClick}
      onChange={onChange}
      onVisibilityChange={onVisibilityChange}
    />
  )
} 
